import React, { useState, useEffect } from 'react';
import { Save, Plus, Eye } from 'lucide-react';
import { apiService } from '../../services/api';

interface AnamneseData {
  historicoFamiliar: string;
  habitosAlimentares: string;
  atividadeFisica: string;
  sono: string;
  stress: string;
  objetivos: string;
  medicamentos: string;
  alergias: string;
  observacoes: string;
}

const Anamnese: React.FC = () => {
  const [formData, setFormData] = useState<AnamneseData>({
    historicoFamiliar: '',
    habitosAlimentares: '',
    atividadeFisica: '',
    sono: '',
    stress: '',
    objetivos: '',
    medicamentos: '',
    alergias: '',
    observacoes: ''
  });
  const [loading, setLoading] = useState(false);
  const [historico, setHistorico] = useState<any[]>([]);
  const [pacienteId] = useState('672c8f123456789012345678'); // ID fixo para teste

  useEffect(() => {
    carregarHistorico();
  }, []);

  const carregarHistorico = async () => {
    try {
      const data = await apiService.getAvaliacoes(pacienteId, 'anamnese');
      setHistorico(data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      // Manter dados mock em caso de erro
      setHistorico([
        {
          _id: '1',
          createdAt: '2024-10-15',
          anamnese: { objetivos: 'Perder peso e melhorar disposição' }
        }
      ]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        paciente: pacienteId,
        tipo: 'anamnese',
        anamnese: formData
      };
      
      await apiService.createAvaliacao(payload);
      
      // Recarregar histórico
      await carregarHistorico();
      
      // Limpar formulário
      setFormData({
        historicoFamiliar: '',
        habitosAlimentares: '',
        atividadeFisica: '',
        sono: '',
        stress: '',
        objetivos: '',
        medicamentos: '',
        alergias: '',
        observacoes: ''
      });

      alert('Anamnese salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar anamnese:', error);
      alert('Erro ao salvar anamnese. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid p-4">
      <div className="row">
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header bg-success text-white">
              <h4 className="mb-0">📝 Nova Anamnese</h4>
              <small>Preencha as informações sobre seus hábitos e histórico</small>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="historico-familiar" className="form-label">Histórico Familiar</label>
                    <textarea
                      id="historico-familiar"
                      className="form-control"
                      name="historicoFamiliar"
                      value={formData.historicoFamiliar}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Doenças na família, hereditariedade..."
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="habitos-alimentares" className="form-label">Hábitos Alimentares</label>
                    <textarea
                      id="habitos-alimentares"
                      className="form-control"
                      name="habitosAlimentares"
                      value={formData.habitosAlimentares}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Rotina alimentar, preferências, restrições..."
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="atividade-fisica" className="form-label">Atividade Física</label>
                    <textarea
                      id="atividade-fisica"
                      className="form-control"
                      name="atividadeFisica"
                      value={formData.atividadeFisica}
                      onChange={handleInputChange}
                      rows={2}
                      placeholder="Frequência, tipo de exercício..."
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="qualidade-sono" className="form-label">Qualidade do Sono</label>
                    <textarea
                      id="qualidade-sono"
                      className="form-control"
                      name="sono"
                      value={formData.sono}
                      onChange={handleInputChange}
                      rows={2}
                      placeholder="Horas de sono, qualidade, dificuldades..."
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="nivel-stress" className="form-label">Nível de Stress</label>
                    <textarea
                      id="nivel-stress"
                      className="form-control"
                      name="stress"
                      value={formData.stress}
                      onChange={handleInputChange}
                      rows={2}
                      placeholder="Fatores de stress, como lida com eles..."
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="medicamentos" className="form-label">Medicamentos</label>
                    <textarea
                      id="medicamentos"
                      className="form-control"
                      name="medicamentos"
                      value={formData.medicamentos}
                      onChange={handleInputChange}
                      rows={2}
                      placeholder="Medicamentos em uso, dosagem..."
                    />
                  </div>
                  <div className="col-12 mb-3">
                    <label htmlFor="alergias" className="form-label">Alergias e Intolerâncias</label>
                    <input
                      id="alergias"
                      type="text"
                      className="form-control"
                      name="alergias"
                      value={formData.alergias}
                      onChange={handleInputChange}
                      placeholder="Alergias alimentares, intolerâncias..."
                    />
                  </div>
                  <div className="col-12 mb-3">
                    <label htmlFor="objetivos" className="form-label">Objetivos</label>
                    <textarea
                      id="objetivos"
                      className="form-control"
                      name="objetivos"
                      value={formData.objetivos}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Seus objetivos com o acompanhamento nutricional..."
                      required
                    />
                  </div>
                  <div className="col-12 mb-3">
                    <label htmlFor="observacoes" className="form-label">Observações Adicionais</label>
                    <textarea
                      id="observacoes"
                      className="form-control"
                      name="observacoes"
                      value={formData.observacoes}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Outras informações importantes..."
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button 
                    type="submit" 
                    className="btn btn-success d-flex align-items-center gap-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <output className="spinner-border spinner-border-sm">
                          <span className="visually-hidden">Salvando...</span>
                        </output>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Save size={20} />
                        Salvar Anamnese
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card shadow-sm">
            <div className="card-header bg-light">
              <h5 className="mb-0">📋 Histórico de Anamneses</h5>
            </div>
            <div className="card-body">
              {historico.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <Plus size={48} className="mb-2" />
                  <p>Nenhuma anamnese registrada</p>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {historico.map((item: any) => (
                    <div key={item._id} className="list-group-item border-0 px-0">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">Anamnese</h6>
                          <p className="mb-1 text-muted small">{item.anamnese?.objetivos || 'Sem objetivos'}</p>
                          <small className="text-success">Concluída</small>
                        </div>
                        <div className="d-flex flex-column align-items-end">
                          <small className="text-muted mb-1">{new Date(item.createdAt).toLocaleDateString('pt-BR')}</small>
                          <button className="btn btn-sm btn-outline-primary">
                            <Eye size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Anamnese;